import { EdlinkIdCode } from '@/components/alphanumeric-code';
import { EditableInfoCard } from '@/components/editable-info-card';
import { SubmitProps } from '@/components/editable-info-page';
import { ManualEdlinkSync } from '@/components/edlink/manual-edlink-sync';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { InfoCard } from '@/components/info-card';
import { InfoKeyValue } from '@/components/info-key-value';
import { InfoPage } from '@/components/info-page';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary';
import { EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';
import { RoleBadgeList } from '@/components/users/role-badge';
import { EmptyArray, getSchool, getSchools, getUsers, updateSchool } from '@/lib/api-calls';
import { airServerErrorFrom, axiosErrorFrom } from '@/lib/error-handling';
import { allPagesModifier, Role, schoolStatusSchema, UserWithRolesAndProfiles } from '@airreading/common/data';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { z } from 'zod';
import { schoolDetailsTabs } from '../-components/tabs';
import { validateRouteRole } from '@/lib/routing';
import { isAirAdmin } from '@airreading/common';
import { FormCheckbox } from '@/components/forms/form-checkbox';

export const Route = createFileRoute('/_layout/orgs/schools/$schoolId/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: SchoolPage,
});

const formSchema = z.object({
  name: z.string(),
  orgId: z.string(),
  schoolStatus: schoolStatusSchema,
  edlinkId: z.string().optional().nullable().default(null),
  canSendSchoolParentReminders: z.boolean().optional().default(false),
});

const columnHelper = createColumnHelper<UserWithRolesAndProfiles>();
const columns = [
  columnHelper.accessor((row) => row.id, {
    id: 'name',
    header: 'Name',
    cell: ({ row, getValue }) => (
      <Link to={'/users/$userId'} params={{ userId: getValue() }}>
        {row.original.fullNameOrEmail}
      </Link>
    ),
  }),
  columnHelper.accessor('email', {
    id: 'email',
    header: 'Email',
  }),
  columnHelper.accessor('phone', {
    id: 'phone',
    header: 'Phone',
  }),
  columnHelper.accessor('timeZone', {
    id: 'timezone',
    header: 'Time zone',
  }),
  columnHelper.accessor('userRoles', {
    id: 'roles',
    header: 'Roles',
    cell: ({ getValue }) => {
      return (
        <div className="flex gap-x-1">
          <RoleBadgeList userRoles={getValue()} />
        </div>
      );
    },
  }),
];

function SchoolPage() {
  return (
    <InfoPageSuspenseBoundary
      onError={(e) => {
        const axiosError = axiosErrorFrom(e);
        const airServerError = airServerErrorFrom(e);

        return {
          allowRetry: axiosError?.response?.status !== 404,
          errorHeader: airServerError?.userFacingMessage || 'Failed to load school. Please try again.',
          back: {
            backLink: {
              to: '/orgs',
            },
          },
        };
      }}
    >
      <SchoolDetails />
    </InfoPageSuspenseBoundary>
  );
}

function SchoolDetails() {
  const userRolesToFetch: Role[] = ['school_admin', 'school_contact'];
  const { schoolId } = Route.useParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data } = useSuspenseQuery(getSchool(schoolId));

  const {
    data: usersResponse,
    isPending: isLoadingUsers,
    error: loadingUserErrors,
    refetch: refetchUsers,
  } = useQuery(
    getUsers({
      ...allPagesModifier,
      filters: {
        schools: [data.id],
        roles: userRolesToFetch,
      },
    }),
  );

  const { mutateAsync } = useMutation({
    mutationFn: updateSchool,
  });

  const onSubmit = ({ formData, disableEditing }: SubmitProps<z.infer<typeof formSchema>>) => {
    return mutateAsync({
      schoolId,
      data: {
        ...formData,
        edlinkId: ((edlinkId) => {
          switch (edlinkId) {
            case null: {
              return null;
            }
            default: {
              if (!edlinkId.trim()) {
                return null;
              }
              return edlinkId;
            }
          }
        })(formData.edlinkId),
      },
    })
      .then(() => {
        toast({
          title: 'School updated successfully',
        });
        disableEditing();
        queryClient.invalidateQueries(getSchools());
        queryClient.invalidateQueries(getSchool(schoolId));
      })
      .catch(() => {
        toast({
          title: 'School update failed',
        });
      });
  };

  const table = useReactTable({
    data: usersResponse?.users || EmptyArray,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <InfoPage
      headerText={data.name || data.id}
      breadcrumbs={{
        linkItems: [
          {
            label: 'Schools',
            link: {
              to: '/orgs/schools',
            },
          },
        ],
        currentPage: data.name,
      }}
      headerActions={
        <>
          {data.edlinkId && (
            <ManualEdlinkSync entityType="school" internalId={data.id} trigger={<Button>Sync from Edlink</Button>} />
          )}
        </>
      }
      tabs={schoolDetailsTabs({ schoolId: data.id })}
    >
      <>
        <EditableInfoCard
          headerText="Basic information"
          formSchema={formSchema}
          onSubmit={onSubmit}
          defaultValues={{
            name: data.name,
            orgId: data.orgId,
            schoolStatus: data.schoolStatus,
            edlinkId: data.edlinkId ?? '',
            canSendSchoolParentReminders: data.canSendSchoolParentReminders ?? false,
          }}
        >
          {({ form, editing }) => {
            return (
              <>
                <InfoKeyValue
                  label="School name"
                  value={data.name}
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormInputField
                        form={form}
                        name="name"
                        label="School name"
                        inputProps={{ placeholder: 'School name' }}
                      />
                    ),
                  }}
                />
                <InfoKeyValue label="District" value={data.org.name} />
                <InfoKeyValue label="Time zone" value={data.org.timeZone || '-'} />
                <InfoKeyValue
                  label="Edlink ID"
                  value={<EdlinkIdCode code={data.edlinkId} />}
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormInputField
                        form={form}
                        name="edlinkId"
                        label="Edlink ID"
                        inputProps={{ className: 'font-mono' }}
                      />
                    ),
                  }}
                />
                <InfoKeyValue
                  label={'Status'}
                  value={data.schoolStatus}
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormSingleSelect
                        name="schoolStatus"
                        label="Status"
                        form={form}
                        options={schoolStatusSchema.options}
                      />
                    ),
                  }}
                />
                <InfoKeyValue
                  label="Enable parent reminder email and SMS (1 hr)"
                  value={data.canSendSchoolParentReminders ? 'Yes' : 'No'}
                  edit={{
                    editing,
                    editModeComponent: (
                      <FormCheckbox
                        name="canSendSchoolParentReminders"
                        label="Enable parent reminder email and SMS (1 hr)"
                        form={form}
                      />
                    ),
                  }}
                />
              </>
            );
          }}
        </EditableInfoCard>

        <InfoCard headerText="School admins & contacts">
          <EntityTable
            table={table}
            isLoading={isLoadingUsers}
            hasError={!!loadingUserErrors}
            emptyState={'No admins or contacts found'}
            errorState={<TableErrorState refetchAction={refetchUsers} />}
            emptyStateClassName="translate-y-0"
          />
        </InfoCard>
      </>
    </InfoPage>
  );
}
